import moment from 'moment'
import { colourForPlaylist } from 'common/playlist-colour'
import { combineDateAndTime, dayOffset, endDayIsChosen, formatLength, formatMoment } from 'common/date-time'
import { NO_STANDING } from '../calendar-intervals'
import { hasUnsavedEdits, saveProblem } from '../panel-draft'
import { coveredByOwnPlaylist } from '../own-playlist-cover'
import { SCREEN_SHARE_NOTES, roleNote, peerNames } from './screen-notes'
import template from './schedule-inspector.tpl.html'
import './schedule-inspector.component.less'

/** The fields that the date inputs and the time inputs edit. */
const TIME_FIELDS = Object.freeze(['startDate', 'startTime', 'endDate', 'endTime'])

/**
 * The panel beside the calendar. It replaced two modals. A modal covers the
 * week. The operator could not see the on-screen strip change while typing an
 * end time. The calendar and the fields are visible at the same time.
 *
 * One form does creation and editing, because the server has one write path.
 * There is no priority field. The operator selects a role, and
 * the server sets a priority from the role.
 *
 * The state manager owns the values. The panel shows the draft and the
 * standing, and it sends each edit back through onEdit.
 */
class ScheduleInspectorController {
  constructor ($scope, $element, $timeout) {
    'ngInject'
    this._$scope = $scope
    this._$element = $element
    this._$timeout = $timeout
    this.playlistSearch = ''

    // ng-model reads and writes the time fields through these functions, with
    // getterSetter. A call without a value reads the draft. A call with a
    // value sends the edit to the state manager.
    this.field = Object.freeze(TIME_FIELDS.reduce((fields, name) => Object.assign(fields, {
      [name]: (...written) => written.length ? this.edit({ [name]: written[0] }) : this.fieldValue(name)
    }), {}))
  }

  /**
   * A new state is a new opening of the panel. A drag of the bar and an edit
   * keep the state, so the search and the scroll of the list stay.
   */
  $onChanges (changes) {
    if (changes.state) {
      this.playlistSearch = ''
    }

    if (changes.state || changes.playlists) {
      this.revealSelectedPlaylist()
    }
  }

  /**
   * Scroll the playlist list to the selected playlist. A scene can have more
   * playlists than the list shows, and the selected one was out of view.
   *
   * The list arrives after the interval, so a new list also scrolls. The
   * selected row gets its class in the digest, so the code waits for it. The
   * wait runs no digest, because a scroll changes no model.
   */
  revealSelectedPlaylist () {
    if (!this.draft || !this.draft.sceneCollectionId) return

    this._$timeout(() => {
      const selected = this._$element[0].querySelector('.schedule-inspector-playlist.is-selected')

      if (selected) {
        selected.scrollIntoView({ block: 'nearest' })
      }
    }, 0, false)
  }

  /** The values that the panel shows and the operator edits, or null. */
  get draft () {
    return this.state ? this.state.draft : null
  }

  /** The band peers and the screen answer for the draft. */
  get standing () {
    return this.state ? this.state.standing : NO_STANDING
  }

  /** The role that the panel shows and that a save sends. */
  get role () {
    return this.standing.role || null
  }

  fieldValue (name) {
    return this.draft ? this.draft[name] : undefined
  }

  edit (changes) {
    this.onEdit({ $changes: changes })
  }

  get isNew () {
    return Boolean(this.draft) && !this.draft.intervalId
  }

  /** True when a save writes something that the stored row does not have. */
  get hasUnsavedChanges () {
    return Boolean(this.state) && hasUnsavedEdits(this.state.baseline, this.state.draft)
  }

  /** The ids can be strings or numbers, so the comparison converts both. */
  get playlist () {
    if (!this.draft || !this.draft.sceneCollectionId) return null

    return (this.playlists || [])
      .find(collection => Number(collection.id) === Number(this.draft.sceneCollectionId)) || null
  }

  /**
   * The colour of the bars of a playlist. The list shows it beside each name,
   * so the list is also the legend of the grid.
   *
   * The rank uses the playlist ids of the last fetch of the week, because the
   * bars got their colours from that fetch. The list loads when the panel
   * opens. A playlist can join or leave the scene while the panel is open, and
   * the list then holds an old set. Before the first fetch the grid has no
   * bars, so the list uses its own playlists.
   */
  colourOf (collection) {
    const ids = this.scenePlaylistIds || (this.playlists || []).map(playlist => playlist.id)

    return colourForPlaylist(collection && collection.id, ids)
  }

  selectPlaylist (collection) {
    if (collection) {
      this.edit({ sceneCollectionId: collection.id })
    }
  }

  /**
   * The end and the length in words, for example "Thu 27/08 02:00 (next day) · 4 h".
   *
   * The midnight rule moves the end day and shows no message. The operator can
   * easily miss the date field. A start moved past the end gives almost 24 hours, and only
   * the length makes that obvious.
   */
  get endReadBack () {
    if (!this.draft) return ''

    const start = combineDateAndTime(this.draft.startDate, this.draft.startTime)
    const end = combineDateAndTime(this.draft.endDate, this.draft.endTime)
    const length = start && end ? formatLength(start, end) : ''

    if (!length) return ''

    // The marker shows the day that the midnight rule gives. A longer interval
    // shows its days in the length.
    const nextDay = dayOffset(this.draft) === 1 && !endDayIsChosen(this.draft) ? ' (next day)' : ''

    return `${formatMoment(end)}${nextDay} · ${length}`
  }

  selectRole (role) {
    this.edit({ role })
  }

  /** The sentence under the role. Without a draft there is none. */
  get roleNote () {
    return this.draft ? roleNote(this.role, this.standing) : ''
  }

  /**
   * True when this interval overlaps peers in its own band. Only one interval
   * in a band goes on screen for the shared stretch.
   */
  get sharesStretch () {
    return Boolean(this.draft) && !this.isNew && Boolean(this.role) &&
      this.standing.peers.length > 0
  }

  /** The peers that share the stretch, while the panel shows the shared-stretch section. */
  get peerNames () {
    return this.sharesStretch ? peerNames(this.standing.peers) : ''
  }

  /**
   * How much of the run a save of the panel values puts on screen, as a
   * SCREEN_SHARE value. Null when the panel cannot tell, for example for an
   * interval that is not written yet.
   *
   * The state manager uses the winner rule of the strip and the ribbon, so the
   * three surfaces give one answer. A request to take the stretch is separate.
   */
  get screenShare () {
    return this.draft && this.standing.screen ? this.standing.screen.share : null
  }

  /**
   * True when every interval that wins a part of the run has the playlist in
   * the panel. The screen then shows that playlist anyway. Preflight makes the
   * same decision with the same function.
   */
  get coveredByOwnPlaylist () {
    const screen = this.draft && this.standing.screen

    return Boolean(screen) && coveredByOwnPlaylist(screen.coveredByPlaylistIds, this.draft.sceneCollectionId)
  }

  /**
   * The note box for the screen share, or null. The entry is a constant, so
   * an Angular watch on it sees no change between two digests.
   */
  get screenShareNote () {
    return SCREEN_SHARE_NOTES[this.screenShare] || null
  }

  /** The text of the note box for the screen share. */
  get screenShareText () {
    const note = this.screenShareNote

    return note ? note.text(this.standing.screen, this.draft.sceneCollectionId) : ''
  }

  /**
   * True when a raise above the band peers puts more of the interval on
   * screen. The button and the promise both read this.
   *
   * The interval can be on screen for part of its run already. It can still
   * lose the stretch it shares with a peer, and the raise wins that stretch.
   * The raise changes only the order in the band, so a higher band can still
   * cover the shared stretch. The shared rule also refuses a raise in the
   * override band and in a full band. In each case the button would do nothing
   * on screen, or end in a refusal.
   *
   * When the own playlist wins every lost part, the raise takes the screen
   * from the same playlist. The screen then does not change.
   */
  get raiseGainsScreen () {
    return this.sharesStretch && this.standing.raiseGainsScreen === true && !this.coveredByOwnPlaylist
  }

  /** True when the panel shows "Put on screen". */
  get offersStretch () {
    return this.raiseGainsScreen && this.draft.winsPeers !== true
  }

  /**
   * True when the operator asked for the stretch, and the panel promises the
   * screen. The request stays in the draft while the operator types, because
   * a time field reports each digit as a time. A save sends it only while the
   * panel shows the promise.
   */
  get promisesScreen () {
    return this.raiseGainsScreen && this.draft.winsPeers === true
  }

  /**
   * Ask for this interval to take the shared stretch. This function writes
   * nothing, because the panel has one save path. On save, the server raises
   * this interval above its peers and does not change the peers.
   */
  takeStretch () {
    if (!this.offersStretch) return
    this.edit({ winsPeers: true })
  }

  /**
   * True when the stored interval is on air at this moment.
   *
   * The test uses the baseline, not the draft. The note warns what a save can
   * change, and that is the interval as stored. Times the operator is still
   * typing describe a stretch that is not playing.
   */
  get coversNow () {
    if (this.isNew || !this.state) {
      return false
    }

    const { baseline } = this.state
    const start = combineDateAndTime(baseline.startDate, baseline.startTime)
    const end = combineDateAndTime(baseline.endDate, baseline.endTime)

    if (!start || !end) {
      return false
    }

    const now = moment()

    // The end is exclusive, the same as everywhere else in the schedule. An
    // interval that ends now is no longer on screen.
    return !now.isBefore(start) && now.isBefore(end)
  }

  /** True while a save of this opening waits for the server. The panel is then read-only. */
  get isSaving () {
    return Boolean(this.state && this.state.saving)
  }

  get blockedBecause () {
    return this.draft ? saveProblem(this.draft, this.standing) : null
  }

  /**
   * The reason in the warning box. A new interval that the operator has not
   * changed gets no warning yet. The warning came before the operator did
   * anything, and Create is disabled in any case.
   */
  get blockedNote () {
    if (this.isNew && !this.hasUnsavedChanges) return null

    return this.blockedBecause
  }

  save () {
    if (this.blockedBecause) return
    // A request that the panel does not show at this moment is not sent.
    this.onSave({ $winsPeers: this.promisesScreen })
  }

  remove () {
    if (this.isNew) {
      this.cancel()
      return
    }
    this.onDelete({ $interval: this.draft })
  }

  cancel () {
    this.onCancel()
  }

  /**
   * Start a takeover over the interval in the panel. The panel then opens on
   * the new takeover, so this is offered only for an interval that exists.
   */
  punchIn () {
    if (this.isNew) return
    this.onPunchIn()
  }

  /**
   * Open the content of the playlist on the scene page. This is a navigation
   * away from the calendar. Unsaved edits in the panel are lost, the same as
   * when the operator closes the calendar.
   */
  openPlaylist () {
    if (!this.draft || !this.draft.sceneCollectionId) return
    this.onOpenPlaylist({ $sceneCollectionId: this.draft.sceneCollectionId })
  }
}

export const ScheduleInspectorComponent = {
  bindings: {
    // The panel state of the state manager: { baseline, draft, endDay,
    // standing }. A new object is a new opening of the panel.
    state: '<',
    playlists: '<',
    // The ids that the bars of the grid got their colours from.
    scenePlaylistIds: '<',
    onEdit: '&',
    onSave: '&',
    onDelete: '&',
    onCancel: '&',
    onPunchIn: '&',
    onOpenPlaylist: '&'
  },
  controller: ScheduleInspectorController,
  template
}
